import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import { TextFieldVerificationProps } from "./TextFieldVerification"

interface TextFieldVerificationStoreProps extends TextFieldVerificationProps {
   inputRef: React.MutableRefObject<HTMLInputElement | null>
   ref: React.ForwardedRef<HTMLInputElement>
}

const validChars = "0-9"

export default class TextFieldVerificationStore {
   sp
   localValue = ''
   isActive = false

   constructor(sp: TextFieldVerificationStoreProps) {
      this.sp = sp
   }

   get hiddenInputProps() {
      return {
         ref: (node: HTMLInputElement | null) => {
            this.sp.inputRef.current = node
            if (typeof this.sp.ref === "function") {
               this.sp.ref(node)
            } else if (this.sp.ref) {
               this.sp.ref.current = node
            }
         },
         onFocus: () => {
            this.isActive = true
         },
         onBlur: () => {
            this.isActive = false
         },
         onSelect: (e: React.SyntheticEvent<HTMLInputElement>) => {
            const target = e.target as HTMLInputElement
            const val = target.value
            target.setSelectionRange(val.length, val.length)
         }
      }
   }

   get state() {
      return [...Array(6)].map((_, i) => ({
         isSelected: (this.sp.value.length === i || (this.sp.value.length === i + 1 && 6 === i + 1)) && this.isActive,
         isInactive: this.sp.value.length < i,
         isFilled: this.sp.value.length > i
      }))
   }

   onPaste = (rawPaste: string) => {
      const pasteText = rawPaste.replace(/\s/g, '').split('').filter(char => /[0-9]/.test(char)).join('')
      if (pasteText && pasteText.length < 7 && this.valueIsValid(pasteText)) {
         const newValue = (this.sp.value += pasteText).slice(0, 6)
         this.updateValue(newValue)
         if (newValue.length === 6) {
            this.sp.onComplete?.(newValue)
         }
      }
   }

   handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newInputVal = event.target.value.replace(/\s/g, "")
      this.updateValue(newInputVal)
   }

   valueIsValid = (value: string) => {
      return RegExp(`^[${validChars}]{0,6}$`).test(value)
   }

   handleClick = () => {
      this.sp.inputRef?.current?.focus()
   }

   handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
         // do not allow to change cursor position
         event.preventDefault()
      }
      
      if (event.key === 'Enter' && this.sp.value.length === 6) {
         this.sp.onComplete?.(this.sp.value)
      }
   }

   updateValue = (newValue: string) => {
      if (this.valueIsValid(newValue)) {
         this.sp.onChange?.(newValue)
      }
   }
}

export const { Provider: TextFieldVerificationStoreProvider, useProvidedStore: useTextFieldVerificationStore } = createStoreContext(TextFieldVerificationStore)
